import { Text } from '@lmc/ui-kit/src/general'
import { Input, Select } from 'antd'
import type React from 'react'
import { useState } from 'react'
import styled from 'styled-components'

export interface InputAndSelectUnitProps {
  unitOptions: { label: string; value: string }[]
  unit: string
  onUnitChange: (unit: string) => void
  value: number | undefined
  onChange: (value: number | undefined) => void
  description?: string
  placeholder?: string
  step?: string | number
  min?: number
  max?: number
}

export const InputAndSelectUnit = ({
  unitOptions,
  unit,
  onUnitChange,
  value,
  onChange,
  description,
  placeholder,
  step = '1',
  min,
  max
}: InputAndSelectUnitProps) => {
  const [errorType, setErrorType] = useState<'empty' | 'range' | null>(null)

  const validateValue = (val: number | undefined) => {
    if (val === undefined) {
      setErrorType('empty')
      return false
    }

    if ((min !== undefined && val < min) || (max !== undefined && val > max)) {
      setErrorType('range')
      return false
    }

    setErrorType(null)
    return true
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    if (errorType) {
      setErrorType(null)
    }

    if (inputValue === '') {
      onChange(undefined)
      return
    }

    const stepValue = typeof step === 'string' ? parseFloat(step) : step
    const decimalPlaces =
      stepValue % 1 === 0 ? 0 : step.toString().split('.')[1]?.length || 0

    if (decimalPlaces === 0) {
      const numValue = parseInt(inputValue, 10)
      if (!isNaN(numValue)) {
        onChange(numValue)
      }
    } else {
      const numValue = parseFloat(inputValue)
      if (!isNaN(numValue)) {
        const multiplier = 10 ** decimalPlaces
        const roundedValue = Math.round(numValue * multiplier) / multiplier
        onChange(roundedValue)
      }
    }
  }

  const handleBlur = () => {
    validateValue(value)
  }

  const handleFocus = () => {
    setErrorType(null)
  }

  const handleUnitChange = (selectedUnit: unknown) => {
    onUnitChange(selectedUnit as string)
  }

  const getErrorMessage = () => {
    if (errorType === 'empty') {
      return 'Field cannot be empty'
    }
    if (errorType === 'range') {
      return `Value must be between ${min} and ${max}`
    }
    return ''
  }

  return (
    <Container>
      <InputContainer $hasError={errorType !== null}>
        <StyledInput
          type="number"
          value={value ?? ''}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
        />
        <SelectWrapper>
          <StyledSelect
            value={unit}
            onChange={handleUnitChange}
            options={unitOptions}
            variant="borderless"
            labelRender={(label) => <Text>{label.label}</Text>}
            optionRender={(option) => <Text>{option.label}</Text>}
          />
        </SelectWrapper>
      </InputContainer>
      {errorType && <ErrorMessage>{getErrorMessage()}</ErrorMessage>}
      {description && !errorType && <Description>{description}</Description>}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 300px;
`

const InputContainer = styled.div<{ $hasError: boolean }>`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  border-radius: 6px;
  overflow: hidden;
  color: ${({ theme }) => theme.color.neutral.color3};

  &:hover {
    border-color: #4096ff;
  }

  &:focus-within {
    border-color: #4096ff;
    box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.1);
  }

  ${({ $hasError, theme }) =>
    $hasError &&
    `
    border-color: ${theme.color.status.danger};
    box-shadow: 0 0 0 2px ${theme.color.status.danger}20;
  `}
`

const StyledInput = styled(Input)`
  border: none;
  border-radius: 0;
  flex: 1;
  padding: 8px 12px;
  border-right: 1px solid ${({ theme }) => theme.color.neutral.color7};

  &:focus {
    box-shadow: none;
  }

  &:hover {
    border-color: transparent;
  }
`

const SelectWrapper = styled.div`
  min-width: 100px;
  color: ${({ theme }) => theme.color.neutral.color2};
`

const StyledSelect = styled(Select)`
  color: ${({ theme }) => theme.color.neutral.color2};
  width: 100%;
`

const Description = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const ErrorMessage = styled(Text)`
  color: ${({ theme }) => theme.color.status.danger};
`

export default InputAndSelectUnit
